import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { PLANS } from "../../lib/plans";

export default function Pricing({ onSelect }) {
  const [entity, setEntity] = useState("sole_trader");
  const userInteractedRef = useRef(false);
  const plans = PLANS[entity];

  // Auto-rotate the entity tab every 5s until the visitor manually picks one
  // or hovers/touches the toggle area. Pauses when the tab is hidden.
  useEffect(() => {
    const id = setInterval(() => {
      if (userInteractedRef.current) return;
      if (typeof document !== "undefined" && document.hidden) return;
      setEntity((prev) => (prev === "sole_trader" ? "company" : "sole_trader"));
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const stopAutoplay = () => {
    userInteractedRef.current = true;
  };

  const handlePick = (next) => {
    stopAutoplay();
    setEntity(next);
  };

  return (
    <section id="pricing" className="py-20 md:py-32" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500">
              Monthly subscriptions · NZD
            </div>
            <h2 className="mt-3 font-heading font-black text-4xl md:text-5xl tracking-tight leading-tight">
              Flat monthly pricing.<br />No surprise invoices.
            </h2>
            <p className="mt-4 text-slate-600 text-lg">
              Pick the entity that matches how you operate. Three tiers each.
            </p>
          </div>
          {/* Custom entity toggle */}
          <div
            className="inline-flex border border-slate-300 bg-white p-1 self-start md:self-end"
            role="tablist"
            aria-label="Entity type"
            onMouseEnter={stopAutoplay}
            onTouchStart={stopAutoplay}
          >
            <button
              role="tab"
              aria-selected={entity === "sole_trader"}
              onClick={() => handlePick("sole_trader")}
              className={`px-5 py-3 text-sm font-heading font-bold uppercase tracking-[0.15em] transition-colors ${
                entity === "sole_trader"
                  ? "bg-[#0A192F] text-white"
                  : "text-slate-700 hover:text-[#0A192F]"
              }`}
              data-testid="pricing-toggle-sole-trader"
            >
              Sole Trader
            </button>
            <button
              role="tab"
              aria-selected={entity === "company"}
              onClick={() => handlePick("company")}
              className={`px-5 py-3 text-sm font-heading font-bold uppercase tracking-[0.15em] transition-colors ${
                entity === "company"
                  ? "bg-[#0A192F] text-white"
                  : "text-slate-700 hover:text-[#0A192F]"
              }`}
              data-testid="pricing-toggle-company"
            >
              Company
            </button>
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={entity + "-notice"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="mt-10"
          >
            {entity === "company" ? (
              <div
                className="border border-slate-200 bg-white p-5 flex items-start gap-4"
                data-testid="pricing-notice-company"
              >
                <div className="w-1.5 self-stretch bg-[#C9A349]" aria-hidden />
                <div className="text-sm text-slate-700 space-y-1">
                  <div>
                    <strong className="text-[#0A192F]">Heads up:</strong> you'll need your own paid
                    Xero subscription. Our pricing covers tax, bookkeeping and advisory — it does{" "}
                    <strong>not</strong> include Xero. We'll help you pick the right Xero plan
                    during onboarding.
                  </div>
                  <div className="text-xs text-slate-500">
                    <strong className="text-slate-700">Pick by shareholder count:</strong>{" "}
                    Professional fits a typical <strong>1–2 shareholder</strong> company.
                    Enterprise is for companies with <strong>3 or more shareholders</strong>.
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="border border-slate-200 bg-white p-5 flex items-start gap-4"
                data-testid="pricing-notice-sole-trader"
              >
                <div className="w-1.5 self-stretch bg-[#C9A349]" aria-hidden />
                <div className="text-sm text-slate-700">
                  <strong className="text-[#0A192F]">Everything included.</strong> Your
                  Prosaic subscription is covered by us — no separate billing, no extra
                  software fees. Sign up and you're fully set up.
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <AnimatePresence mode="wait">
          <motion.div
            key={entity}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className="mt-6 grid md:grid-cols-3 gap-5"
          >
            {plans.map((p) => (
              <PlanCard
                key={p.id}
                plan={p}
                entity={entity}
                onSelect={() => onSelect(entity, p)}
              />
            ))}
          </motion.div>
        </AnimatePresence>

        <div className="mt-8 text-sm text-slate-500 space-y-1">
          <div>
            <strong className="text-slate-700">All prices are NZD, GST exclusive.</strong> 15% NZ
            GST will be added on your tax invoice.{" "}
            <strong className="text-slate-700">Month-to-month — cancel any time.</strong> Your plan
            scope is aligned with the NZ financial year (1 April → 31 March), but you only ever pay
            for the months you stay subscribed. No lock-in.
          </div>
          <div>
            <strong className="text-slate-700">Prior-year tax not filed?</strong> Add our catch-up
            service at checkout — a one-off fee equal to 3 months of your plan.
          </div>
          <div>
            <strong className="text-slate-700">Companies:</strong> you'll need your own paid Xero
            subscription — it's not included in our pricing.{" "}
            <strong className="text-slate-700">Sole traders:</strong> your Prosaic
            subscription is fully covered by us — Prosaic also lets you raise simple invoices to
            your customers, though it isn't a full accounts-receivable system.
          </div>
        </div>
      </div>
    </section>
  );
}

function PlanCard({ plan, entity, onSelect }) {
  const highlight = plan.highlight;
  return (
    <div
      className={`relative flex flex-col border p-8 transition-transform hover:-translate-y-1 ${
        highlight
          ? "bg-[#0A192F] border-[#0A192F] text-white"
          : "bg-white border-slate-200 text-[#0F172A]"
      }`}
      data-testid={`plan-card-${entity}-${plan.id}`}
    >
      {highlight && (
        <div className="absolute -top-3 left-8 bg-[#C9A349] text-white text-[10px] font-bold uppercase tracking-[0.22em] px-3 py-1">
          Most popular
        </div>
      )}
      <div
        className={`text-xs uppercase tracking-[0.22em] font-bold ${
          highlight ? "text-[#C9A349]" : "text-slate-500"
        }`}
      >
        {plan.name}
      </div>
      <div className="mt-5 flex items-baseline gap-1">
        <span className="font-heading font-black text-5xl tracking-tight">${plan.price}</span>
        <span className={`text-sm ${highlight ? "text-slate-300" : "text-slate-500"}`}>
          + GST / month
        </span>
      </div>
      <p
        className={`mt-3 text-sm ${
          highlight ? "text-slate-300" : "text-slate-600"
        }`}
      >
        {plan.tagline}
      </p>
      <ul className="mt-6 space-y-3 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm">
            <Check
              className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                highlight ? "text-[#C9A349]" : "text-[#C9A349]"
              }`}
            />
            <span className={highlight ? "text-slate-100" : "text-slate-700"}>{f}</span>
          </li>
        ))}
      </ul>
      <button
        onClick={onSelect}
        className={`mt-8 py-3.5 font-medium transition-colors ${
          highlight
            ? "bg-[#C9A349] text-white hover:bg-white hover:text-[#0A192F]"
            : "bg-[#0A192F] text-white hover:bg-[#C9A349]"
        }`}
        data-testid={`plan-cta-${entity}-${plan.id}`}
      >
        Get started — ${plan.price} + GST/mo
      </button>
    </div>
  );
}
